import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api, Goal, Match } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { LiveNotice } from '../components/LiveNotice';
import { StadiumBoard } from '../components/StadiumBoard';
import { MatchExtra } from '../components/MatchExtra';
import { MatchPreview } from '../components/MatchPreview';
import { TimbaPick } from '../components/TimbaPick';
import { Flag, refDisplay } from '../components/TeamBadge';
import { formatKickoff } from '../lib/format';
import { venue } from '../lib/venues';
import { teamName } from '../lib/flags';
import { useMyTimbaSlots } from '../lib/useMyTimbaSlots';
import { SlotDisp } from '../lib/timbaBracket';
import { TeamRef } from '../api/client';
import { H2, Muted, BackLink, Pill } from '../components/ui';

const Scoreboard = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 22px 14px;
  text-align: center;
  .side { display: flex; flex-direction: column; align-items: center; gap: 8px; }
  .nm { font-weight: 700; }
  .sc { font-size: 2.4rem; font-weight: 800; }
`;

const BoardBtn = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.accentText};
  border: none;
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px;
  font-weight: 700;
  margin-bottom: 12px;
`;

const Goals = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.85rem;
  .col:last-child { text-align: right; }
  .g { color: ${({ theme }) => theme.colors.textMuted}; }
`;

const VenueCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin-top: 8px;
  .name { font-weight: 700; font-size: 1.05rem; }
`;

function goalLine(g: Goal): string {
  const tag = g.penalty ? ' (p)' : g.owngoal ? ' (e/c)' : '';
  return `${g.name} ${g.minute ?? ''}'${tag}`;
}

function statusLabel(m: Match): string {
  if (m.status === 'live') return m.live?.clock ? `EN JUEGO · ${m.live.clock}` : 'EN JUEGO';
  if (m.status === 'finished' && m.score) return 'Finalizado';
  return formatKickoff(m.kickoffUtc);
}

export function MatchDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const timbaSlots = useMyTimbaSlots();
  const [boardClosed, setBoardClosed] = useState(false);
  useEffect(() => setBoardClosed(false), [id]); // al cambiar de partido, reabrir la pizarra
  const state = useAsync(
    () => api.match(id),
    [id],
    `/match/${id}`,
    (d) => d.match.status === 'live',
  );
  // Datos ricos de ESPN (stats/alineaciones/eventos). Refresca mientras el partido está en vivo.
  const detail = useAsync(
    () => api.matchDetail(id),
    [id],
    undefined,
    () => state.data?.match.status === 'live',
  );

  return (
    <>
      <BackLink onClick={() => navigate(-1)}>← Volver</BackLink>
      <Async state={state}>
        {(d) => {
          const m = d.match;
          // En la llave, mostrar los rivales tentativos (cascada de mis pronósticos) en vez de
          // "Ganador del #101". Fuera de la llave (o antes de cargar), el ref normal del partido.
          const pair = m.phase === 'knockout' ? timbaSlots.get(m.num as number) : undefined;
          const disp = (s: SlotDisp | undefined, ref: TeamRef): { team: string | null; text: string } =>
            s && (s.team || s.label)
              ? { team: s.team, text: s.team ? teamName(s.team) : s.label }
              : { team: refDisplay(ref).team, text: refDisplay(ref).text };
          const t1 = disp(pair?.[0], m.team1);
          const t2 = disp(pair?.[1], m.team2);
          const v = venue(m.ground);
          const live = m.status === 'live';
          return (
            <>
              {live && !boardClosed && <StadiumBoard match={m} onClose={() => setBoardClosed(true)} />}

              <LiveNotice status={d.liveStatus} hasLive={live} />

              {live && boardClosed && (
                <BoardBtn onClick={() => setBoardClosed(false)}>📺 Ver pizarra en vivo</BoardBtn>
              )}

              <div style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
                <Pill>{m.phase === 'group' ? `Grupo ${m.group}` : m.stage?.label}</Pill>
                <Pill>{m.round}</Pill>
              </div>

              <Scoreboard>
                <div className="side">
                  <Flag team={t1.team} size={44} />
                  <div className="nm">{t1.text}</div>
                </div>
                <div>
                  <div className="sc">{m.score ? `${m.score.ft[0]}–${m.score.ft[1]}` : 'vs'}</div>
                  <Muted>{statusLabel(m)}</Muted>
                  {m.score?.ht && (
                    <Muted>
                      ET: {m.score.ht[0]}–{m.score.ht[1]}
                    </Muted>
                  )}
                </div>
                <div className="side">
                  <Flag team={t2.team} size={44} />
                  <div className="nm">{t2.text}</div>
                </div>
              </Scoreboard>

              {(m.goals1.length > 0 || m.goals2.length > 0) && (
                <Goals>
                  <div className="col">
                    {m.goals1.map((g, i) => (
                      <div className="g" key={i}>
                        {goalLine(g)}
                      </div>
                    ))}
                  </div>
                  <div className="col">
                    {m.goals2.map((g, i) => (
                      <div className="g" key={i}>
                        {goalLine(g)}
                      </div>
                    ))}
                  </div>
                </Goals>
              )}

              {detail.data?.available && (
                <>
                  <MatchPreview
                    data={detail.data}
                    t1={{ name: t1.text, flag: t1.team ?? t1.text }}
                    t2={{ name: t2.text, flag: t2.team ?? t2.text }}
                    venue={v}
                  />
                  <MatchExtra data={detail.data} team1={t1.text} team2={t2.text} />
                </>
              )}

              {m.phase === 'knockout' && <TimbaPick match={m} />}

              <H2>Sede</H2>
              {v ? (
                <VenueCard>
                  <div className="name">{v.stadium}</div>
                  <Muted>
                    {v.city}, {v.country} {v.flag} · Capacidad ~{v.capacity.toLocaleString('es-AR')}
                  </Muted>
                </VenueCard>
              ) : (
                <Muted>{m.ground ?? 'Sede a confirmar'}</Muted>
              )}

              <H2>Horario</H2>
              <Muted>{formatKickoff(m.kickoffUtc)} (hora local de tu dispositivo)</Muted>
            </>
          );
        }}
      </Async>
    </>
  );
}
